import { useQueryClient } from "@tanstack/react-query";
import { useApi } from "./useApi";
import { useModeAwareMutation, useModeAwareQuery } from "./useModeAwareQuery";

interface CaseTransactionIdsResponse {
	caseId: number;
	transactionIds: number[];
}

export function useCaseTransactionIds(caseId: string | undefined) {
	const { apiGet } = useApi();

	return useModeAwareQuery<number[], Error>(
		["case-transaction-ids", caseId],
		async ({ signal }) => {
			if (!caseId) return [];
			const response = await apiGet<CaseTransactionIdsResponse>(
				`/api/v1/collection-cases/${caseId}/transactions`,
				undefined,
				signal,
			);
			return response.transactionIds;
		},
		[],
		{ enabled: !!caseId, staleTime: 30 * 1000, refetchOnWindowFocus: false },
	);
}

interface UpdateCaseTransactionsRequest {
	caseId: string;
	transactionIds: number[];
}

export function useUpdateCaseTransactions() {
	const { apiPut } = useApi();
	const queryClient = useQueryClient();

	return useModeAwareMutation<
		CaseTransactionIdsResponse,
		Error,
		UpdateCaseTransactionsRequest
	>(
		async (request) => {
			return apiPut<CaseTransactionIdsResponse>(
				`/api/v1/collection-cases/${request.caseId}/transactions`,
				{ transactionIds: request.transactionIds },
			);
		},
		(request) => {
			return {
				caseId: Number(request.caseId),
				transactionIds: request.transactionIds,
			};
		},
		{
			onSuccess: (_data, variables) => {
				// Invalidate the case-transaction-ids query to refetch from server
				// Use partial match to handle the extended query key (includes mode and companyId)
				queryClient.invalidateQueries({ 
					queryKey: ["case-transaction-ids", variables.caseId],
					exact: false,
				});
				queryClient.invalidateQueries({ queryKey: ["case-transactions"] });
				queryClient.invalidateQueries({ queryKey: ["case-audit-logs"] });
			},
		},
	);
}
